import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { crmDeleteLead, crmUpdateLead, formatApiError } from "../../lib/crm";

export const ROLE_LABELS = {
  MISS_AURA: "Miss Aura",
  EMBAIXADORA: "Embaixadora",
  PROFESSIONAL: "Profissional",
  CLIENT: "Cliente",
};

export const STATUSES = ["NOVO", "EM_ANALISE", "ENTREVISTA", "APROVADA", "REPROVADA"];

export const STATUS_META = {
  NOVO: { label: "Novo", classes: "bg-gold-light text-gold border-gold-border" },
  EM_ANALISE: { label: "Em análise", classes: "bg-teal-light text-teal-deep border-teal/20" },
  ENTREVISTA: { label: "Entrevista", classes: "bg-champagne/60 text-ink border-gold-border" },
  APROVADA: { label: "Aprovada", classes: "bg-teal text-white border-teal" },
  REPROVADA: { label: "Reprovada", classes: "bg-red-50 text-red-600 border-red-200" },
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) +
    " · " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

const FIELDS = [
  ["phone", "Telefone / WhatsApp"],
  ["email", "E-mail"],
  ["church", "Igreja / Ministério / Liderança"],
  ["city", "Cidade / Estado"],
  ["instagram", "Instagram"],
  ["profession", "Profissão / Especialidade"],
  ["statement", "Declaração de Intenção"],
];

export const LeadDetail = ({ lead, open, onOpenChange, onUpdated, onDeleted }) => {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!lead) return null;

  const deleteLead = async () => {
    setDeleting(true);
    try {
      await crmDeleteLead(lead.id);
      toast.success(`Candidatura de ${lead.name} excluída.`);
      onDeleted(lead.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setDeleting(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const { data } = await crmUpdateLead(lead.id, { note: note.trim() });
      setNote("");
      toast.success("Observação registrada.");
      onUpdated(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="crm-lead-detail"
        className="max-h-[90vh] max-w-md overflow-y-auto border-gold-border bg-cream sm:max-w-2xl"
      >
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${STATUS_META[lead.status]?.classes || ""}`}>
              {STATUS_META[lead.status]?.label || lead.status}
            </span>
            <span className="rounded-full bg-teal-light px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-teal-deep">
              {ROLE_LABELS[lead.role] || lead.role}
            </span>
          </div>
          <DialogTitle className="font-serif text-3xl text-ink">{lead.name}</DialogTitle>
        </DialogHeader>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-mutedink">Cadastro</p>
            <p className="mt-1 text-sm text-ink">{formatDate(lead.created_at)}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-mutedink">Origem</p>
            <p className="mt-1 text-sm text-ink">{lead.source === "landing" ? "Landing Page" : lead.source || "—"}</p>
          </div>
          {FIELDS.map(([key, label]) =>
            lead[key] ? (
              <div key={key} className={key === "statement" ? "sm:col-span-2" : ""}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-mutedink">{label}</p>
                <p className="mt-1 text-sm leading-relaxed text-ink">{lead[key]}</p>
              </div>
            ) : null
          )}
          {(lead.referral_code || lead.preferred_miss_aura_code) && (
            <div className="sm:col-span-2 rounded-2xl border border-gold-border bg-gold-light/60 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">Indicação</p>
              <div className="mt-2 space-y-1 text-sm text-ink">
                {lead.referral_code && <p>Código de indicação (ref): <span className="font-semibold">{lead.referral_code}</span></p>}
                {lead.preferred_miss_aura_code && (
                  <p>Convite de Miss Aura (refAmb): <span className="font-semibold">{lead.preferred_miss_aura_code}</span>
                    {lead.miss_aura_name && <> — <span className="font-semibold">{lead.miss_aura_name}</span></>}
                  </p>
                )}
              </div>
            </div>
          )}
          {lead.consent?.accepted && (
            <div data-testid="crm-lead-consent" className="sm:col-span-2 rounded-2xl border border-teal/20 bg-teal-light p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-teal-deep">Consentimento LGPD</p>
              <p className="mt-2 text-sm text-ink">
                Aceite registrado em {formatDate(lead.consent.accepted_at)} · Termos v{lead.consent.terms_version} · Política v{lead.consent.privacy_version}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-mutedink">Observações internas</p>
          <div className="mt-3 space-y-3">
            {(lead.notes || []).map((n, i) => (
              <div key={i} className="rounded-xl border border-gold-border bg-white p-3">
                <p className="text-sm text-ink">{n.text}</p>
                <p className="mt-1 text-[10px] text-mutedink">{formatDate(n.at)} · {n.by}</p>
              </div>
            ))}
            {(lead.notes || []).length === 0 && (
              <p className="text-sm text-mutedink">Nenhuma observação ainda.</p>
            )}
          </div>
          <div className="mt-3 flex gap-2">
            <textarea
              data-testid="crm-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Escreva uma observação interna..."
              className="min-h-[70px] flex-1 rounded-xl border border-gold-border bg-white px-3 py-2 text-sm text-ink outline-none focus:border-teal"
            />
          </div>
          <button
            data-testid="crm-note-add"
            onClick={addNote}
            disabled={saving || !note.trim()}
            className="mt-2 rounded-full bg-teal px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-teal-hover disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Adicionar observação"}
          </button>
        </div>

        <div className="mt-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-mutedink">Histórico</p>
          <div className="mt-3 space-y-2 border-l-2 border-gold-border pl-4">
            {(lead.history || []).map((h, i) => (
              <div key={i}>
                <p className="text-sm text-ink">{h.action}</p>
                <p className="text-[10px] text-mutedink">{formatDate(h.at)}{h.by ? ` · ${h.by}` : ""}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 border-t border-gold-border pt-5">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="crm-lead-delete-btn"
                disabled={deleting}
                className="flex items-center gap-2 rounded-full border border-red-200 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Excluir candidatura
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="crm-lead-delete-confirm" className="border-gold-border bg-cream">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif text-2xl text-ink">Excluir candidatura?</AlertDialogTitle>
                <AlertDialogDescription className="text-sm text-mutedink">
                  A candidatura de <span className="font-semibold text-ink">{lead.name}</span> será removida
                  permanentemente, incluindo observações e histórico. Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="crm-lead-delete-cancel" className="rounded-full border-gold-border">Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="crm-lead-delete-confirm-btn"
                  onClick={deleteLead}
                  className="rounded-full bg-red-600 text-white hover:bg-red-700"
                >
                  Sim, excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </DialogContent>
    </Dialog>
  );
};
